import { useState } from 'react'
import AppLayout from '../components/AppLayout'
import { Alert, Badge, Button, Card } from '../components/ui'
import {
  formatDateTime,
  useAdminConfirmEmail,
  useAdminDisableMfa,
  useAdminUnlock,
  useAdminUsers,
  useSendPasswordReset,
  type AdminUser,
} from '../lib/admin'
import RequireSession from './RequireSession'

export default function AdminUsersPage() {
  return (
    <RequireSession adminOnly>
      {(user) => (
        <AppLayout user={user}>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Gestion des utilisateurs
          </h1>
          <UsersTable />
        </AppLayout>
      )}
    </RequireSession>
  )
}

function UsersTable() {
  const users = useAdminUsers()
  const sendReset = useSendPasswordReset()
  const disableMfa = useAdminDisableMfa()
  const confirmEmail = useAdminConfirmEmail()
  const unlock = useAdminUnlock()
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  if (users.isPending) {
    return <p className="mt-6 text-sm text-neutral-500">Chargement...</p>
  }

  if (users.isError) {
    return (
      <div className="mt-6">
        <Alert>{users.error.message}</Alert>
      </div>
    )
  }

  const onSendReset = (target: AdminUser) => {
    setNotice(null)
    sendReset.mutate(target.userId, {
      onSuccess: () =>
        setNotice({ tone: 'success', text: `Lien de reinitialisation envoye a ${target.email}.` }),
      onError: (error) => setNotice({ tone: 'error', text: error.message }),
    })
  }

  const onDisableMfa = (target: AdminUser) => {
    if (!window.confirm(`Desactiver la verification en deux etapes de ${target.email} ?`)) {
      return
    }

    setNotice(null)
    disableMfa.mutate(target.userId, {
      onSuccess: () =>
        setNotice({
          tone: 'success',
          text: `Verification en deux etapes desactivee pour ${target.email}. L'utilisateur en a ete prevenu par e-mail.`,
        }),
      onError: (error) => setNotice({ tone: 'error', text: error.message }),
    })
  }

  const onConfirmEmail = (target: AdminUser) => {
    // L'administrateur se porte garant de l'adresse : on le lui rappelle.
    if (!window.confirm(`Confirmer l'adresse ${target.email} sans lien e-mail ? Faites-le seulement si vous etes sur qu'elle appartient a cet utilisateur.`)) {
      return
    }

    setNotice(null)
    confirmEmail.mutate(target.userId, {
      onSuccess: () =>
        setNotice({ tone: 'success', text: `Adresse ${target.email} confirmee : l'utilisateur peut se connecter.` }),
      onError: (error) => setNotice({ tone: 'error', text: error.message }),
    })
  }

  const onUnlock = (target: AdminUser) => {
    setNotice(null)
    unlock.mutate(target.userId, {
      onSuccess: () => setNotice({ tone: 'success', text: `Compte ${target.email} deverrouille.` }),
      onError: (error) => setNotice({ tone: 'error', text: error.message }),
    })
  }

  const busy = sendReset.isPending || disableMfa.isPending || confirmEmail.isPending || unlock.isPending

  return (
    <div className="mt-6 space-y-4">
      {notice !== null && <Alert tone={notice.tone}>{notice.text}</Alert>}

      <Card>
        <div className="-mx-6 -my-6 overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead className="border-b border-neutral-200 text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
              <tr>
                <th className="px-6 py-3 font-medium">Utilisateur</th>
                <th className="px-3 py-3 font-medium">Etat</th>
                <th className="px-3 py-3 font-medium">Derniere connexion</th>
                <th className="px-6 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {users.data.map((user) => (
                <tr key={user.userId}>
                  <td className="px-6 py-3">
                    <div className="font-medium text-neutral-900 dark:text-neutral-100">
                      {user.displayName}
                    </div>
                    <div className="text-neutral-500 dark:text-neutral-400">{user.email}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.isAdmin && <Badge tone="neutral">Admin</Badge>}
                      {user.emailConfirmed ? (
                        <Badge tone="ok">E-mail confirme</Badge>
                      ) : (
                        <Badge tone="warn">E-mail non confirme</Badge>
                      )}
                      {user.twoFactorEnabled ? (
                        <Badge tone="ok">2FA</Badge>
                      ) : (
                        <Badge tone="neutral">Sans 2FA</Badge>
                      )}
                      {!user.hasPassword && <Badge tone="warn">Sans mot de passe</Badge>}
                      {user.lockedOutUntil !== null && <Badge tone="warn">Verrouille</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400">
                    {formatDateTime(user.lastSignInAt)}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      {!user.emailConfirmed && (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() => onConfirmEmail(user)}
                        >
                          Confirmer l'adresse
                        </Button>
                      )}
                      {user.lockedOutUntil !== null && (
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          disabled={busy}
                          onClick={() => onUnlock(user)}
                        >
                          Deverrouiller
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => onSendReset(user)}
                      >
                        Reinitialiser le mot de passe
                      </Button>
                      {user.twoFactorEnabled && (
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          disabled={busy}
                          onClick={() => onDisableMfa(user)}
                        >
                          Desactiver la 2FA
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
